/**
 * SickLeaveAddModal.js
 *
 * Modal for adding sick leave for a single day or a date range.
 * Department is selected first; the staff list is then filtered to
 * that department. A date mode toggle switches between single day
 * and date range entry.
 *
 * Service-level gate errors are surfaced as inline feedback:
 * - 'Kein geplanter Dienst für diesen Tag'              → warning banner (yellow)
 * - 'Kein geplanter Dienst für den gewählten Zeitraum'  → warning banner (yellow)
 * - 'Für diesen Tag existiert bereits...'               → error banner  (red)
 *
 * SL-V3-1: Range result string promoted to a styled inline notification
 * banner. Full success (skipped === 0) renders green. Partial success
 * (skipped > 0) renders amber, matching the .warning-banner color language.
 * role="status" and aria-live="polite" added so screen readers announce
 * the result after save. No logic changes.
 *
 * Mirrors: VacationModal.js
 * Differences:
 * - No edit mode
 * - No preview section
 * - Department → Staff field order (department-first)
 * - Date mode toggle: single day vs date range
 * - Staff and departments sourced from props (pre-filtered by the page)
 * - Full i18n — no hardcoded German strings
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSickLeave } from '../../contexts/SickLeaveContext';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/vacation-modal.css';
import '../../styles/sick-leave.css';

const SickLeaveAddModal = ({
  isOpen,
  onClose,
  departments = [],
  staff = [],
  preselectedStaffId = null,
  preselectedDepartmentId = null,
  preselectedDate = null
}) => {
  const { t } = useTranslation('sickLeave');
  const { currentUser } = useAuth();
  const { addSickLeave, addSickLeaveRange, loading } = useSickLeave();

  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [dateMode, setDateMode] = useState('single'); // 'single' | 'range'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [rangeResult, setRangeResult] = useState(null); // { added, skipped }

  const [errors, setErrors] = useState({});
  const [showValidation, setShowValidation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Reset all form state when modal opens.
   */
  useEffect(() => {
    if (isOpen) {
      setSelectedDepartmentId(preselectedDepartmentId || '');
      setSelectedStaffId(preselectedStaffId || '');
      setSelectedDate(preselectedDate ? formatDateForInput(preselectedDate) : '');
      setDateMode('single');
      setStartDate('');
      setEndDate('');
      setRangeResult(null);
      setErrors({});
      setShowValidation(false);
    }
  }, [isOpen, preselectedStaffId, preselectedDepartmentId, preselectedDate]);

  /**
   * Fallback auto-populate: if a staff member is selected but no department
   * is set (e.g. via preselectedStaffId without preselectedDepartmentId),
   * populate department from staff's primaryDepartmentId.
   * In normal flow department is selected first so this rarely fires.
   */
  useEffect(() => {
    if (selectedStaffId && !selectedDepartmentId) {
      const staffMember = staff.find(s => s.id === selectedStaffId);
      if (staffMember?.primaryDepartmentId) {
        setSelectedDepartmentId(staffMember.primaryDepartmentId);
      } else if (staffMember?.departmentIds?.length > 0) {
        setSelectedDepartmentId(staffMember.departmentIds[0]);
      }
    }
  }, [selectedStaffId, selectedDepartmentId, staff]);

  /**
   * Filter the staff list to only those belonging to the selected department.
   * Department drives this — falls back to full staff list when no department
   * is selected yet.
   */
  const filteredStaff = useMemo(() => {
    if (!selectedDepartmentId) return staff;
    return staff.filter(s => s.departmentIds?.includes(selectedDepartmentId));
  }, [staff, selectedDepartmentId]);

  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedDepartmentId) {
      newErrors.department = t('department') + ' ' +
        t('isRequired', { defaultValue: 'is required' });
    }

    if (!selectedStaffId) {
      newErrors.staff = t('staffMember') + ' ' +
        t('isRequired', { defaultValue: 'is required' });
    }

    if (dateMode === 'single') {
      if (!selectedDate) {
        newErrors.date = t('date') + ' ' +
          t('isRequired', { defaultValue: 'is required' });
      }
    } else {
      if (!startDate) {
        newErrors.startDate = t('startDate', { defaultValue: 'Start date' }) + ' ' +
          t('isRequired', { defaultValue: 'is required' });
      }
      if (!endDate) {
        newErrors.endDate = t('endDate', { defaultValue: 'End date' }) + ' ' +
          t('isRequired', { defaultValue: 'is required' });
      }
      if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
        newErrors.dateRange = t('endDateAfterStart', {
          defaultValue: 'End date must be after start date'
        });
      }
    }

    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Department changes first — clears staff so the dropdown repopulates
   * with members from the newly selected department.
   */
  const handleDepartmentChange = (e) => {
    setSelectedDepartmentId(e.target.value);
    setSelectedStaffId('');
    setErrors(prev => ({
      ...prev,
      department: undefined,
      staff: undefined,
      submit: undefined,
      noShift: undefined
    }));
  };

  /**
   * Staff selection — department is already set so no reset needed.
   */
  const handleStaffChange = (e) => {
    setSelectedStaffId(e.target.value);
    setErrors(prev => ({
      ...prev,
      staff: undefined,
      submit: undefined,
      noShift: undefined
    }));
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setErrors(prev => ({
      ...prev,
      date: undefined,
      submit: undefined,
      noShift: undefined
    }));
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setErrors(prev => ({
      ...prev,
      startDate: undefined,
      dateRange: undefined,
      noShift: undefined
    }));
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setErrors(prev => ({
      ...prev,
      endDate: undefined,
      dateRange: undefined,
      noShift: undefined
    }));
  };

  const handleDateModeChange = (mode) => {
    setDateMode(mode);
    setErrors(prev => ({
      ...prev,
      date: undefined,
      startDate: undefined,
      endDate: undefined,
      dateRange: undefined,
      noShift: undefined
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowValidation(true);

    if (!validateForm()) return;

    setIsSaving(true);
    setErrors(prev => ({ ...prev, submit: undefined, noShift: undefined }));

    try {
      if (dateMode === 'single') {
        await addSickLeave({
          staffId: selectedStaffId,
          departmentId: selectedDepartmentId,
          date: selectedDate,
          createdBy: currentUser.uid
        });
        onClose();
      } else {
        const result = await addSickLeaveRange({
          staffId: selectedStaffId,
          departmentId: selectedDepartmentId,
          startDate,
          endDate,
          createdBy: currentUser.uid
        });
        setRangeResult(result);
        setTimeout(() => onClose(), 1500);
      }
    } catch (err) {
      const message = err.message || '';

      /**
       * Service throws these specific German strings as gate signals.
       * The UI translates them to the active i18n locale via their
       * corresponding sickLeave namespace keys.
       */
      if (
        message === 'Kein geplanter Dienst für diesen Tag' ||
        message === 'Kein geplanter Dienst für den gewählten Zeitraum'
      ) {
        setErrors(prev => ({ ...prev, noShift: t('noShiftOnDay') }));
      } else if (message.startsWith('Für diesen Tag existiert bereits')) {
        setErrors(prev => ({ ...prev, submit: t('duplicateEntry') }));
      } else {
        setErrors(prev => ({ ...prev, submit: t('addError') }));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  if (!isOpen) return null;

  return (
    <div className="vacation-modal-overlay" onClick={handleBackdropClick}>
      <div className="vacation-modal">

        {/* Header */}
        <div className="vacation-modal-header">
          <h2>{t('addSickDay')}</h2>
          <button
            type="button"
            className="vacation-modal-close"
            onClick={handleClose}
            disabled={isSaving}
            aria-label={t('close', { ns: 'common', defaultValue: 'Close' })}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="vacation-modal-form">

          {/* 1 — Department Selection (first field) */}
          <div className="form-group">
            <label htmlFor="sl-modal-department">
              {t('department')} <span className="required">*</span>
            </label>
            <select
              id="sl-modal-department"
              value={selectedDepartmentId}
              onChange={handleDepartmentChange}
              disabled={isSaving}
              className={showValidation && errors.department ? 'error' : ''}
            >
              <option value="">{t('selectDepartment')}</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>
            {showValidation && errors.department && (
              <span className="error-message">{errors.department}</span>
            )}
          </div>

          {/* 2 — Staff Selection (filtered by selected department) */}
          <div className="form-group">
            <label htmlFor="sl-modal-staff">
              {t('staffMember')} <span className="required">*</span>
            </label>
            <select
              id="sl-modal-staff"
              value={selectedStaffId}
              onChange={handleStaffChange}
              disabled={isSaving || !selectedDepartmentId}
              className={showValidation && errors.staff ? 'error' : ''}
            >
              <option value="">{t('selectStaff')}</option>
              {filteredStaff.map(member => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </select>
            {showValidation && errors.staff && (
              <span className="error-message">{errors.staff}</span>
            )}
          </div>

          {/* 3 — Date Mode Toggle */}
          <div className="form-group">
            <label>{t('dateMode', { defaultValue: 'Date Mode' })}</label>
            <div className="date-mode-toggle">
              <button
                type="button"
                className={`btn ${dateMode === 'single' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => handleDateModeChange('single')}
                disabled={isSaving}
              >
                {t('dateModeSingle', { defaultValue: 'Single Day' })}
              </button>
              <button
                type="button"
                className={`btn ${dateMode === 'range' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => handleDateModeChange('range')}
                disabled={isSaving}
              >
                {t('dateModeRange', { defaultValue: 'Date Range' })}
              </button>
            </div>
          </div>

          {/* 4a — Single Date Input */}
          {dateMode === 'single' && (
            <div className="form-group">
              <label htmlFor="sl-modal-date">
                {t('date')} <span className="required">*</span>
              </label>
              <input
                type="date"
                id="sl-modal-date"
                value={selectedDate}
                onChange={handleDateChange}
                disabled={isSaving}
                className={showValidation && errors.date ? 'error' : ''}
              />
              {showValidation && errors.date && (
                <span className="error-message">{errors.date}</span>
              )}
            </div>
          )}

          {/* 4b — Date Range Inputs */}
          {dateMode === 'range' && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="sl-modal-start-date">
                  {t('startDate', { defaultValue: 'Start Date' })} <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="sl-modal-start-date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  disabled={isSaving}
                  className={showValidation && errors.startDate ? 'error' : ''}
                />
                {showValidation && errors.startDate && (
                  <span className="error-message">{errors.startDate}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="sl-modal-end-date">
                  {t('endDate', { defaultValue: 'End Date' })} <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="sl-modal-end-date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  disabled={isSaving}
                  className={showValidation && errors.endDate ? 'error' : ''}
                />
                {showValidation && errors.endDate && (
                  <span className="error-message">{errors.endDate}</span>
                )}
              </div>
              {showValidation && errors.dateRange && (
                <span className="error-message form-row-error">{errors.dateRange}</span>
              )}
            </div>
          )}

          {/* 5 — Range result notification banner */}
          {rangeResult && (
            <div
              className={
                `sl-result-banner ${
                  rangeResult.skipped === 0
                    ? 'sl-result-banner--success'
                    : 'sl-result-banner--partial'
                }`
              }
              role="status"
              aria-live="polite"
            >
              {t('rangeAddResult', {
                added: rangeResult.added,
                skipped: rangeResult.skipped,
                defaultValue: `${rangeResult.added} day(s) added, ${rangeResult.skipped} skipped (no shift)`
              })}
            </div>
          )}

          {/* 6 — No-shift warning (shift-gate from service layer) */}
          {errors.noShift && (
            <div className="warning-banner">{errors.noShift}</div>
          )}

          {/* 7 — Duplicate or generic submit error */}
          {errors.submit && (
            <div className="error-banner">{errors.submit}</div>
          )}

          {/* 8 — Actions */}
          <div className="vacation-modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={isSaving}
            >
              {t('cancel', { ns: 'common', defaultValue: 'Cancel' })}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSaving || loading || rangeResult !== null}
            >
              {isSaving
                ? t('saving', { ns: 'common', defaultValue: 'Saving...' })
                : t('save', { ns: 'common', defaultValue: 'Save' })}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default SickLeaveAddModal;